/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogContent } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Attachment } from '@src/app/model/attachment';
import { ConfirmDialogService } from '@src/app/shared/dialogs/confirmDialog';
import { DialogBannerComponent } from '@src/app/shared/dialogs/dialog-banner-component';
import { ByteCountPipe } from '@src/app/shared/pipes/byte-count.pipe';
import { FileTypeIconComponent } from '@src/app/shared/file-type-icon/file-type-icon.component';

const WARN_BYTES = 7 * 1024 * 1024;
const LIMIT_BYTES = 9.5 * 1024 * 1024;

export const ATTACH_WARN_TEXT =
    `Warning: This file is likely too large to send by mail, you may get errors if you attempt to email it`;

export const ATTACH_LIMIT_TEXT =
    `This file is too large to be able to send directly by email. If you are sending this to a system user, ` +
    `advise them instead they need to login to view the file. If your mail recipient is not a system user ` +
    `you will need to find an alternate way to share the file such as google drive or onedrive`;

export interface PickAttachmentDialogData {
    attachments: Attachment[];
    title?: string;
}

@Component({
    selector: 'app-pick-attachment-dialog',
    templateUrl: './pick-attachment-dialog.component.html',
    styleUrls: ['./pick-attachment-dialog.component.scss'],
    imports: [MatIconModule, MatDialogContent, MatTooltipModule, DialogBannerComponent, ByteCountPipe, FileTypeIconComponent],
})
export class PickAttachmentDialogComponent {
    dialogRef = inject<MatDialogRef<PickAttachmentDialogComponent>>(MatDialogRef);
    data = inject<PickAttachmentDialogData>(MAT_DIALOG_DATA);
    cds = inject(ConfirmDialogService);

    readonly warnText = ATTACH_WARN_TEXT;
    readonly limitText = ATTACH_LIMIT_TEXT;

    warnLevel(attach: Attachment): 0 | 1 | 2 {
        if (attach.size > LIMIT_BYTES) return 2;
        if (attach.size > WARN_BYTES) return 1;
        return 0;
    }

    pickAttachment(attach: Attachment): void {
        const level = this.warnLevel(attach);
        if (level === 0) {
            this.dialogRef.close(attach);
        } else if (level === 1) {
            this.cds.openConfirm('File Size Warning', this.warnText).subscribe(result => {
                this.dialogRef.close(result === 'OK' ? attach : null);
            });
        } else {
            this.cds.alert('File Too Large', this.limitText).afterClosed().subscribe(() => {
                this.dialogRef.close(null);
            });
        }
    }
}
